import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Film } from 'lucide-react';
import { videoUrl } from '../../help/videoUrl';
import './DocVideo.css';

/**
 * A docs / onboarding video from the CDN (`help/videoUrl.js`), shared by the
 * onboarding carousel and the Help articles.
 *
 * - A short clip (`narrated` false) is muted, loops and autoplays — the way a
 *   page may play something without asking.
 * - A narrated one sits behind the native play button with controls and
 *   English captions (conventions: narrated clips never autoplay).
 * - Offline, or before the owner has uploaded a clip (it 404s), the poster and
 *   a short note stand in; if the poster is missing too, a neutral plate does.
 *
 * Captions come from another origin, so the narrated video is requested with
 * `crossOrigin="anonymous"`. If the CDN does not answer that with CORS the
 * load fails; the component then retries ONCE without it — the video plays,
 * only the captions are lost.
 */
export default function DocVideo({ id, narrated = false, className = '', label }) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(() => typeof navigator !== 'undefined' && navigator.onLine === false);
  const [posterFailed, setPosterFailed] = useState(false);
  const [cors, setCors] = useState(narrated);

  // A different clip is a fresh attempt.
  useEffect(() => {
    setFailed(typeof navigator !== 'undefined' && navigator.onLine === false);
    setPosterFailed(false);
    setCors(narrated);
  }, [id, narrated]);

  if (!id) return null;
  const poster = videoUrl(id, 'poster');

  const onVideoError = () => {
    if (cors) setCors(false);
    else setFailed(true);
  };

  return (
    <div className={`doc-video${className ? ` ${className}` : ''}`} data-video-id={id}>
      {!failed ? (
        <video
          key={`${id}-${cors ? 'cors' : 'plain'}`}
          className="doc-video-media"
          src={videoUrl(id, 'web')}
          poster={poster}
          aria-label={label || undefined}
          crossOrigin={cors ? 'anonymous' : undefined}
          autoPlay={!narrated}
          muted={!narrated}
          loop={!narrated}
          controls={narrated}
          playsInline
          preload={narrated ? 'metadata' : 'auto'}
          onError={onVideoError}
        >
          {narrated && cors && (
            <track kind="captions" src={videoUrl(id, 'captions')} srcLang="en" label="English" default />
          )}
        </video>
      ) : (
        <div className="doc-video-fallback" role="img" aria-label={label || t('help.videoUnavailable')}>
          {!posterFailed && (
            <img className="doc-video-media" src={poster} alt="" onError={() => setPosterFailed(true)} />
          )}
          <span className="doc-video-note">
            <Film size={13} aria-hidden="true" />
            {t('help.videoUnavailable')}
          </span>
        </div>
      )}
    </div>
  );
}
